import React from 'react'

import {
    Card,
    CardContent,
    Typography,
    Grid,
    Box,
    Divider,
} from '@mui/material'
import {
    CalendarToday,
    PersonOutline,
    Business,
    ChatBubbleOutline,
    Description,
} from '@material-ui/icons'
import { Badge, IconButton, Theme } from '@material-ui/core'

import {
    FormStatus,
    RemovePropertyForm,
    AddPropertyForm,
} from '../../../models/Forms'
import { toMMDDYYYY } from '../../../helpers'
import { FormStatusBadge } from '../../../components/FormStatusBadge'
import { Container } from '../../../components'
import { FormStatusTransitionButtons, TransitionMethod } from './FormButtons'
import { UpdateFormRequest } from '../../../hooks/useAddRemovePropertyForms'

interface Props {
    form: RemovePropertyForm
    theme: Theme
    handleOpenMessageDrawer: (form: RemovePropertyForm) => void
    handleUpdateForm: (request: UpdateFormRequest) => Promise<void>
}

export const RemovePropertyFormRow = ({
    form,
    theme,
    handleOpenMessageDrawer,
    handleUpdateForm,
}: Props) => {
    const handleTransition: TransitionMethod = async (
        event: React.MouseEvent<HTMLButtonElement>,
        formData: AddPropertyForm | RemovePropertyForm,
        status: FormStatus,
    ) => {
        await handleUpdateForm({
            id: formData.id,
            body: { status },
        })
    }

    return (
        <Card
            elevation={1}
            sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                },
            }}
            style={{
                margin: theme.spacing(1),
            }}
        >
            <CardContent sx={{ p: 1, pb: '8px !important' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ fontWeight: 600 }}
                    >
                        {form.organization.name}
                    </Typography>

                    <Container style={{ flex: 1 }} />

                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation()
                            handleOpenMessageDrawer(form)
                        }}
                        style={{ marginRight: theme.spacing(1) }}
                    >
                        <Badge
                            badgeContent={form.message_count}
                            color={
                                (form.message_count ?? 0) > 0
                                    ? 'primary'
                                    : 'secondary'
                            }
                        >
                            <ChatBubbleOutline />
                        </Badge>
                    </IconButton>

                    <FormStatusBadge
                        status={form.status}
                        transitions={form.transitions}
                        createdDate={form.created_date}
                    />
                </div>

                <Divider sx={{ mb: 1.5 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Business
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body2">
                                Companies to Remove:{' '}
                                {form.companies
                                    .map((company) => company.name)
                                    .join(', ')}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 1,
                            }}
                        >
                            <Description
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body2">
                                Reason: {form.reason}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <PersonOutline
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body2">
                                Created By: {form.created_by.name}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <CalendarToday
                                style={{
                                    marginRight: 8,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography variant="body2">
                                Created:{' '}
                                {toMMDDYYYY(new Date(form.created_date))}
                            </Typography>
                        </Box>
                        {form.termination_date && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                }}
                            >
                                <CalendarToday
                                    style={{
                                        marginRight: 8,
                                        color: 'primary.main',
                                    }}
                                />
                                <Typography variant="body2">
                                    Termination Date:{' '}
                                    {toMMDDYYYY(
                                        new Date(form.termination_date),
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                <FormStatusTransitionButtons
                    form={form}
                    style={{ marginBottom: 0 }}
                    handleTransition={handleTransition}
                />
            </CardContent>
        </Card>
    )
}
